import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileSignature, ShieldCheck } from "lucide-react";

/**
 * PartnerReviewPage (finbpm-flow-004) — FinBPM's tokenized review surface.
 * The URL token is the credential (opaque, hash-at-rest, expiring; minted
 * per delivery and rotated on re-send). State-changing calls carry the token
 * in the BODY. Shows the signed snapshot side-by-side with the current
 * portal record — Milen's manual review gate before countersignature.
 */
const Row = ({ label, signed, current }) => {
  const drift = (signed ?? "—") !== (current ?? "—");
  return (
    <tr className={drift ? "bg-amber-50" : ""}>
      <td className="p-2 text-slate-600">{label}</td>
      <td className="p-2 text-slate-900 font-medium">{signed ?? "—"}</td>
      <td className="p-2 text-slate-900">{current ?? "—"}</td>
      <td className="p-2">{drift && <Badge className="bg-amber-100 text-amber-800 border-amber-300">drift</Badge>}</td>
    </tr>
  );
};

const PartnerReviewPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [typedName, setTypedName] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/partner/agreement/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "This review link is invalid or has expired.");
      }
    })();
  }, [token]);

  const decide = async (action) => {
    setSubmitting(true);
    try {
      await api.post("/partner/agreement/decision", {
        token,
        action,
        typed_name: typedName.trim() || undefined,
        client_facing_reason: reason.trim() || undefined,
      });
      setDone(action);
      toast.success(action === "approve" ? "Agreement countersigned" : "Decision recorded");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Could not record the decision");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <Card className="bg-white border-slate-200 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="text-amber-600 mx-auto mb-3" size={28} />
            <p className="text-slate-800">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-pulse h-24 w-80 bg-slate-200 rounded" /></div>;
  }

  const a = data.agreement;
  const s = data.signed_snapshot || {};
  const c = data.current_portal_record || {};
  const decided = done || (a.partner_signed_at && "approve") || (a.partner_rejected_at && "reject");

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <Card className="bg-white border-slate-200" data-testid="partner-review-card">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <FileSignature size={20} /> Participation agreement review
            </CardTitle>
            <CardDescription className="text-slate-600">
              Terms version {a.terms_version} · Elected amount{" "}
              <span className="font-semibold">${Number(a.amount_elected).toLocaleString()}</span>
              {Number(a.amount_elected) < Number(data.minimum_amount) && (
                <Badge className="ml-2 bg-amber-100 text-amber-800 border-amber-300">below standard minimum</Badge>
              )}
              {a.pilot_label && <span className="block mt-1 text-amber-700">{a.pilot_label}</span>}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-2">
              Investor signed by typing <span className="font-semibold text-slate-900">{a.investor_typed_name}</span>{" "}
              on {a.investor_signed_at && new Date(a.investor_signed_at).toLocaleString()}.
            </p>
            {data.signed_pdf_available && (
              <Button variant="outline" className="mb-3 border-slate-300"
                onClick={async () => {
                  try {
                    const r = await api.get(`/partner/agreement/${token}/document.pdf`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(r.data);
                    window.open(url, "_blank", "noopener");
                    // The new tab holds its own reference; revoke ours after
                    // it has had a chance to load (repeat clicks otherwise leak).
                    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
                  } catch {
                    toast.error("Could not load the signed document");
                  }
                }}
                data-testid="review-signed-pdf-btn">
                View the signed agreement (PDF)
              </Button>
            )}
            <table className="w-full text-sm border border-slate-200 rounded">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500">
                  <th className="p-2">Field</th><th className="p-2">As signed</th><th className="p-2">Portal record now</th><th className="p-2" />
                </tr>
              </thead>
              <tbody>
                <Row label="Legal name" signed={s.full_name} current={c.full_name} />
                {/* CAPMARKETS-052: the contracting party the document names —
                    for entity electors this legitimately differs from the
                    investor's personal name and must read as elected, not
                    as drift. Server-computed on both sides. */}
                <Row label="Participating party" signed={data.signing_party?.as_signed} current={data.signing_party?.current} />
                <Row label="Email" signed={s.email} current={c.email} />
                <Row label="Phone" signed={s.phone} current={c.phone} />
                <Row label="KYC" signed={s.kyc_status} current={c.kyc_status} />
                <Row label="Accredited" signed={s.accredited_status} current={c.accredited_status} />
                <Row label="File" signed={s.vc_number} current={c.vc_number} />
              </tbody>
            </table>
          </CardContent>
        </Card>

        {decided ? (
          <Card className="bg-white border-slate-200" data-testid="partner-review-decided">
            <CardContent className="p-6 flex items-center gap-3">
              <CheckCircle2 className="text-emerald-600" size={22} />
              <p className="text-slate-800">
                {decided === "approve"
                  ? "This agreement is countersigned. The investor and EchoSense have received the executed copies."
                  : "This agreement was returned to the investor for corrections."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-slate-200" data-testid="partner-review-actions">
            <CardHeader>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <ShieldCheck size={20} /> Decision
              </CardTitle>
              <CardDescription className="text-slate-600">
                Verify the submission against the portal record before accepting.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-900">Countersign — type the FinBPM signatory name</Label>
                <Input
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className="mt-1 bg-white text-slate-900 border-slate-300"
                  data-testid="partner-typed-name"
                />
                <Button
                  onClick={() => decide("approve")}
                  disabled={submitting || !typedName.trim()}
                  className="mt-2 bg-emerald-700 text-white hover:bg-emerald-800"
                  data-testid="partner-approve-btn"
                >
                  Approve & countersign
                </Button>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <Label className="text-slate-900">Reject / request corrections — reason shown to the investor</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="mt-1 bg-white text-slate-900 border-slate-300"
                  data-testid="partner-reject-reason"
                />
                <Button
                  onClick={() => decide("reject")}
                  disabled={submitting || !reason.trim()}
                  variant="outline"
                  className="mt-2 border-amber-400 text-amber-800 hover:bg-amber-50"
                  data-testid="partner-reject-btn"
                >
                  Return for corrections
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PartnerReviewPage;
